<template>
  <div
    class="offcanvas offcanvas-end"
    id="OffcanvasForm"
    ref="offcanvas"
    data-bs-backdrop="static"
  >
    <div class="bg-body-secondary border offcanvas-header py-2">
      {{ type === 'CREATE'? $t('Create Table'): $t('Edit Table')  }}
      <button
        type="button"
        style="font-size: small;"
        class="btn-close shadow-none text-reset"
        data-bs-dismiss="offcanvas"
        aria-label="Close"
      />
    </div>

    <div
      class="offcanvas-body"
      v-if="loading"
    >
      <placeholder-content :is-load="true" />
    </div>
    <div
      class="offcanvas-body"
      v-else-if="!hasPermission"
    >
      <access-denied/>
    </div>
    <div
      class="offcanvas-body p-0"
      v-else
    >
      <div class="border-bottom mb-3 px-3 py-2">
        <i class="fa-solid fa-circle-info me-2" />
        {{ $t('Table Information') }}
      </div>
      <div class="px-3">
        <div class="row g-3 py-2">
          <div class="col-12">
            <label class="form-label">
              {{ $t('Branch') }}
              <span class="text-danger">*</span>
            </label>
            <div class="custom-input-group custom-input-group input-group input-group-sm">
              <select2
                style="width: 100%;"
                @select="() => {
                  form.zone_id = '';
                }"
                v-model="v$.form.branch_id.$model"
                :options="select2Branches.options"
                :settings="select2Branches.setting"
                :placeholder="$t('Select Branch') + '...'"
              />
              <div v-for="error of v$.form.branch_id.$errors"
                class="invalid-feedback"
                :key="error.$uid"
              >
                {{ error.$message }}
              </div>
            </div>
          </div>
          <div class="col-12">
            <label class="form-label">
              {{ $t('Zone') }}
              <span class="text-danger">*</span>
            </label>
            <div class="custom-input-group custom-input-group input-group input-group-sm">
              <select2
                style="width: 100%;"
                v-model="v$.form.zone_id.$model"
                :options="select2Zones.options"
                :settings="select2Zones.setting"
                :placeholder="$t('Select Branch') + '...'"
                :disabled="form.branch_id=='' || form.branch_id==null"
              />
              <div v-for="error of v$.form.zone_id.$errors"
                class="invalid-feedback"
                :key="error.$uid"
              >
                {{ error.$message }}
              </div>
            </div>
          </div>
          <div class="col-md-12">
            <label
              for="name"
              class="form-label"
            >
              {{ $t('Table Name') }}
              <span class="text-danger">*</span>
            </label>
            <div class="input-group-sm">
              <input
                id="name"
                type="text"
                v-model.trim="v$.form.name.$model"
                class="form-control shadow-none"
                :placeholder="$t('Enter table name') + '...'"
              >
              <div v-for="error of v$.form.name.$errors"
                class="d-block invalid-feedback"
                :key="error.$uid"
              >
                {{ error.$message }}
              </div>
            </div>
          </div>
          <div class="col-md-12">
            <label
              for="seat_capacity"
              class="form-label"
            >
              {{ $t('Seating Capacity') }}
              <span class="text-danger">*</span>
            </label>
            <div class="input-group-sm">
              <input
                id="seat_capacity"
                type="number"
                v-model.trim="v$.form.seat_capacity.$model"
                class="form-control shadow-none"
                :placeholder="$t('Enter table seat capacity') + '...'"
              >
              <div v-for="error of v$.form.seat_capacity.$errors"
                class="d-block invalid-feedback"
                :key="error.$uid"
              >
                {{ error.$message }}
              </div>
            </div>
          </div>
          <div class="col-12">
            <label class="form-label">
              {{ $t('Status') }}
              <span class="text-danger">*</span>
            </label>
            <div class="custom-input-group custom-input-group input-group input-group-sm">
              <select2
                style="width: 100%;"
                v-model="v$.form.state.$model"
                :options="select2States.options"
                :settings="select2States.setting"
                :placeholder="$t('Select States') + '...'"
              />
              <div v-for="error of v$.form.branch_id.$errors"
                class="invalid-feedback"
                :key="error.$uid"
              >
                {{ error.$message }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      class="bg-light border-top justify-content-center modal-footer py-2"
      v-if="!loading && hasPermission"
    >
      <ladda
        text="Save"
        v-if="type === 'CREATE'"
        :btn-class="'btn btn-primary btn-block '"
        :loading="loadingSave"
        icon="<i class='fa-solid fa-floppy-disk'></i>"
        :disabled="v$.$invalid"
        @click="handleCreate"
      />
      <ladda
        text="Save"
        v-if="type === 'EDIT'"
        :btn-class="'btn btn-primary btn-block '"
        :loading="loadingSave"
        icon="<i class='fa-solid fa-floppy-disk'></i>"
        :disabled="v$.$invalid"
        @click="handleEdit"
      />
      <button
        type="button"
        data-bs-dismiss="offcanvas"
        class="border-dark-subtle btn-sm btn btn-light me-2 ms-2"
      >
        {{ $t('Cancel') }}
      </button>
    </div>
  </div>
</template>

<script lang="js">
import PlaceholderContent from '@/plugins/PlaceholderContent.vue';
import AccessDenied from "@/plugins/AccessDenied.vue";
import Ladda from '@/plugins/Ladda.vue';
import { useVuelidate } from '@vuelidate/core';
import validation from "./assets/template/validation";
import { Tables } from '@/services/endpoint/tables.service';
import { AUTH, API_URLS } from '@/services/endpoint/_constants';
import numeral from 'numeral';
import Select2 from 'vue3-select2-component';
import signature  from '@/services/signature';

export default {
  setup: () => ({ v$: useVuelidate() }),
  validationConfig: {
    $lazy: true,
  },
  validations() {
    return validation(this.form)
  },
  props: {
    type: {
      type: String,
      default: 'CREATE'
    },
    id: {
      type: Number,
      default: 0
    },
  },
  data() {
    let vm = this;
    return {
      select2Branches: {
        options: [],
        setting: {
          minimumResultsForSearch: Infinity,
          allowClear: true
        }
      },
      select2Zones: {
        options: [],
        setting: {
          dropdownParent: '#OffcanvasForm',
          minimumResultsForSearch: true,
          multiple: false,
          allowClear: true,
          language: {
            loadingMore: function (e) {
              return '<i class="fa-solid fa-spinner fa-spin" />'
            }
          },
          ajax: {
            beforeSend: function (request) {
              request.setRequestHeader('Authorization', 'Bearer ' + AUTH.TOKEN);
            },
            url: AUTH.URL + API_URLS.API_TABLE_ZONE,
            data: function (params) {
              let query = {
                search: params.term,
                page: params.page || 1,
                limit: 10
              };
              (vm.form.branch_id)? query.branch_id = vm.form.branch_id: '';
              return signature($.param(query)).substring(1);
            },
            processResults: function (data, params) {
              let option = data.data;
              let pageCurrent = data.meta.current_page;
              option = option.map(function(item) {
                return {
                  id: item.id,
                  text: vm.$t(item.name)
                }
              });
              return {
                results: option,
                pagination: {
                  more: (pageCurrent * 10) < data.meta.total
                }
              }
            }
          }
        }
      },
      select2States: {
        options: [],
        setting: {
          minimumResultsForSearch: Infinity,
        }
      },
      loading: true,
      hasPermission: false,
      loadingSave: false,
      form: {
        name: '',
        seat_capacity: '',
        branch_id: '',
        zone_id: '',
        state: ''
      }
    }
  },
  components: {
    PlaceholderContent,
    Ladda,
    AccessDenied,
    Select2
  },
  methods: {
    formatNumber(value) {
      return numeral(value).format("0,0");
    },
    formatNumberTwoDigit(value) {
      return numeral(value).format("0,0.00");
    },
    async getCreateData() {
      await Tables.getCreate().then(res => {
        let vm = this;
        this.select2Branches.options = res.data.data.branches.map(function(item) {
          return {
            id: item.id,
            text: vm.$t(item.name)
          }
        });
        this.select2States.options = res.data.data.states.map(function(item) {
          return {
            id: item.value,
            text: vm.$t(item.text)
          }
        });
        this.form.state = res.data.data.states[0].value;
        this.hasPermission = true;
      }).catch(err => {
        if (err?.response?.status === 403) {
          this.hasPermission = false;
          this.loading = false;
        }
			  throw err;
      });
    },
    async getUpdateData() {
      await Tables.getUpdate(this.id).then(res => {
        let vm = this;
        this.select2Branches.options = res.data.filter.branches.map(function(item) {
          return {
            id: item.id,
            text: vm.$t(item.name)
          }
        });
        this.select2Zones.options = res.data.filter.zones.map(function(item) {
          return {
            id: item.id,
            text: vm.$t(item.name)
          }
        });
        this.select2States.options = res.data.filter.states.map(function(item) {
          return {
            id: item.value,
            text: vm.$t(item.text)
          }
        });
        this.form = {
          name: res.data.data.name,
          seat_capacity: res.data.data.seat_capacity,
          branch_id: res.data.data.branch_id,
          zone_id: res.data.data.zone_id,
          state: res.data.data.state
        };
        this.hasPermission = true;
      }).catch(err => {
        if (err?.response?.status === 403) {
          this.hasPermission = false;
          this.loading = false;
        }
			  throw err;
      });
    },
    setParamsBeforeSendCreate() {
      let params = _.cloneDeep(this.form);
      return $.each(params, (key, value) => {
        if (value === null || value === "" || value.length === 0) {
          delete params[key];
        }
      });
    },
    setParamsBeforeSendUpdate() {
      let params = _.cloneDeep(this.form);
      return $.each(params, (key, value) => {
        if (value === null || value === "" || value.length === 0) {
          delete params[key];
        }
      });
    },
    async handleEdit() {
      this.loadingSave = true;
      this.v$.$touch();
      if(this.v$.$invalid) {
        this.loadingSave = false;
        return;
      }
      await Tables.update(this.id, this.setParamsBeforeSendUpdate()).then(res => {
        this.$parent.fetchData();
        this.$toast.success("<B>" + this.$t('Success') + "</B>\n" + this.$t('Update Table!'));
        $(this.$refs.offcanvas).offcanvas('hide');
        this.loadingSave = false;
      }).catch(err => {
        if (err?.response?.data?.message) {
          this.$toast.warn(err?.response?.data?.message);
        }
        this.loadingSave = false;
      });
    },
    async handleCreate() {
      this.loadingSave = true;
      this.v$.$touch();
      if(this.v$.$invalid) {
        this.loadingSave = false;
        return;
      }
      this.loadingSave = false;
      await Tables.create(this.setParamsBeforeSendCreate()).then(res => {
        this.$parent.fetchData();
        this.$toast.success("<B>" + this.$t('Success') + "</B>\n" + this.$t('Save Table!'));
        $(this.$refs.offcanvas).offcanvas('hide');
        this.loadingSave = false;
      }).catch(err => {
        if (err?.response?.data?.message) {
          this.$toast.warn(err?.response?.data?.message);
        }
        this.loadingSave = false;
      });
    },
  },
  async mounted() {
    $(this.$refs.offcanvas).offcanvas('show');

    $(this.$refs.offcanvas).on('shown.bs.offcanvas',async () => {
      this.loading = true;
      try {
        if (this.type === 'CREATE') await this.getCreateData();
        if (this.type === 'EDIT') await this.getUpdateData();
        this.loading = false;
      } catch (err) {
      }
    });

    $(this.$refs.offcanvas).on('hidden.bs.offcanvas', () => {
      this.$parent.offcanvasFroms.open = false;
    });
  },
  computed: {
  }
}
</script>
